import _isEmpty from 'lodash/isEmpty';

import MESSAGES from 'constants/messages';
import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import { getCurrentPage } from 'store/utils/pagination';
import {
  calculateFinalParams,
  calculateNewFilterOption,
} from 'store/utils/handleParams';
import { getHasMachineLearningEnabled } from 'store/services/project/selectors';
import { getUtterancesList } from 'store/services/intentUtterances/actions';
import {
  partialExportAction,
  cancelPartialExportAction,
  partialImportAndMergeAction,
  partialImportAndReplaceAction,
} from 'store/shared/actions';
import * as api from './api';
import * as types from './actionTypes';
import * as selectors from './selectors';

/**
 * Check if an intent already exists
 *
 * @param fieldName
 * @param fieldValue
 * @param exception
 * @returns {Function}
 */
export const checkIntentName = (
  fieldName,
  fieldValue,
  exception = null,
) => async () => {
  try {
    const { data } = await api.checkIntentNameRequest(fieldValue, exception);
    if (data.count > 0) {
      // eslint-disable-next-line no-throw-literal
      throw {
        [fieldName]: MESSAGES.INTENTS.NAME.EXISTS,
      };
    }
  } catch (err) {
    throw err;
  }
};

/**
 * Get All intents
 *
 * @param params
 */
export const getAllIntents = (params = {}) => async dispatch => {
  try {
    dispatch({
      type: types.GET_INTENT_ALL_REQUEST,
    });
    const { data } = await api.getIntentsRequest(params);
    dispatch({
      type: types.GET_INTENT_ALL_SUCCESS,
      payload: {
        items: data.results,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_INTENT_ALL_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Get paginated intents
 *
 * @param paginationParams
 * @param filterParams
 */
export const getIntentsList = (
  paginationParams = {},
  filterParams = {},
) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.GET_INTENT_LIST_REQUEST,
    });
    const state = getState();
    let filterOpt = filterParams;

    if (_isEmpty(filterOpt)) {
      filterOpt = selectors.getFilterParams(state);
    }

    const paginationOptions = {
      ...selectors.getPaginationOptions(state),
      ...paginationParams,
    };
    const filterOptions = {
      ...selectors.getFilterParams(state),
      ...filterOpt,
    };

    const finalParams = calculateFinalParams(filterOptions, paginationOptions);

    dispatch({
      type: types.UPDATE_INTENT_LIST_OPTIONS,
      payload: {
        paginationOptions,
        filterOptions: calculateNewFilterOption(
          selectors.getFilterOptions(state),
          filterOpt,
        ),
        filterParams: filterOptions,
      },
    });

    const { data } = await api.getIntentsRequest(finalParams);

    await dispatch({
      type: types.GET_INTENT_LIST_SUCCESS,
      payload: {
        items: data.results,
        count: data.count,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_INTENT_LIST_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add an intent
 *
 * @param data
 */
export const addIntent = data => async dispatch => {
  try {
    await dispatch({
      type: types.ADD_INTENT_REQUEST,
    });

    const response = await api.addIntentRequest(data);

    await dispatch({
      type: types.ADD_INTENT_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    await dispatch(getIntentsList({ page: 1 }));

    Notify.success({
      title: 'Intent was created successfully',
    });

    return Promise.resolve(response.data.id);
  } catch (error) {
    await dispatch({
      type: types.ADD_INTENT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to add Intent',
      description: ErrorHandler.getNotificationError(error),
    });

    return Promise.reject();
  }
};

/**
 * Get intent
 *
 * @param id
 */
export const getIntent = id => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.GET_INTENT_REQUEST,
    });

    const { data } = await api.getIntentRequest(id);

    await dispatch({
      type: types.GET_INTENT_SUCCESS,
      payload: {
        item: data,
      },
    });

    if (getHasMachineLearningEnabled(getState())) {
      await dispatch(getUtterancesList());
    }
  } catch (error) {
    await dispatch({
      type: types.GET_INTENT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update intent
 *
 * @param id
 * @param data
 */
export const updateIntent = (id, data) => async dispatch => {
  try {
    dispatch({
      type: types.UPDATE_INTENT_REQUEST,
    });

    const response = await api.updateIntentRequest(id, data);

    dispatch({
      type: types.UPDATE_INTENT_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    await dispatch(getIntentsList());

    Notify.success({
      title: 'Intent was updated successfully',
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_INTENT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Intent',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Partial update intent
 *
 * @param id
 * @param data
 */
export const partialUpdateIntent = (id, data) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.PARTIAL_UPDATE_INTENT_REQUEST,
    });

    const response = await api.partialUpdateIntentRequest(id, data);

    await dispatch({
      type: types.PARTIAL_UPDATE_INTENT_SUCCESS,
    });

    await dispatch(getIntentsList());

    // Update current intent if it is open
    if (selectors.getIntent(getState()).id === id) {
      await dispatch({
        type: types.UPDATE_INTENT_SUCCESS,
        payload: {
          item: response.data,
        },
      });
    }
  } catch (error) {
    await dispatch({
      type: types.PARTIAL_UPDATE_INTENT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Delete an intent
 *
 * @param id
 */
export const deleteIntent = id => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.DELETE_INTENT_REQUEST,
    });

    await api.deleteIntentRequest(id);

    await dispatch({
      type: types.DELETE_INTENT_SUCCESS,
    });

    await dispatch(
      getIntentsList({
        page: getCurrentPage(
          selectors.getPaginationOptions(getState()).page,
          selectors.getIntentsList(getState()),
        ),
      }),
    );

    Notify.success({
      title: 'Intent was deleted successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.DELETE_INTENT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to delete Intent',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Train a model
 */
export const train = () => async dispatch => {
  try {
    dispatch({
      type: types.TRAIN_REQUEST,
    });

    await api.trainRequest();

    dispatch({
      type: types.TRAIN_SUCCESS,
    });
  } catch (error) {
    const isTimeoutError = error.code === 'ECONNABORTED';

    const errorMessage = isTimeoutError
      ? 'Request timeout error'
      : ErrorHandler.parseErrors(error.response.data);

    dispatch({
      type: types.TRAIN_ERROR,
      payload: {
        error: errorMessage,
      },
    });
  }
};

export const partialExportSingleIntent = partialExportAction({
  path: 'intents',
  filename: 'intent',
  types: {
    complete: types.PARTIAL_EXPORT_INTENT_COMPLETE,
    request: types.PARTIAL_EXPORT_INTENT_REQUEST,
  },
});

export const partialExportIntents = partialExportAction({
  path: 'intents',
  types: {
    complete: types.PARTIAL_EXPORT_INTENT_COMPLETE,
    request: types.PARTIAL_EXPORT_INTENT_REQUEST,
  },
});

export const cancelExportIntents = cancelPartialExportAction({
  path: 'intents',
  types: {
    cancel: types.PARTIAL_EXPORT_INTENT_CANCEL,
  },
});

export const partialImportAndMergeIntents = partialImportAndMergeAction({
  path: 'intents',
  postHook: dispatch => dispatch(getIntentsList({ page: 1 })),
  types: {
    complete: types.PARTIAL_IMPORT_INTENT_COMPLETE,
    request: types.PARTIAL_IMPORT_INTENT_REQUEST,
  },
});

export const partialImportAndReplaceIntents = partialImportAndReplaceAction({
  path: 'intents',
  postHook: dispatch => dispatch(getIntentsList({ page: 1 })),
  types: {
    complete: types.PARTIAL_IMPORT_INTENT_COMPLETE,
    request: types.PARTIAL_IMPORT_INTENT_REQUEST,
  },
});
/**
 * Select an intent
 *
 * @param item
 */
export const selectIntent = item => ({
  type: types.SELECT_INTENT,
  payload: {
    item,
  },
});

/**
 * Deselect an intent
 */
export const deselectIntent = () => ({
  type: types.DESELECT_INTENT,
});
